import { addMonitorBreadcrumb } from "./monitor-breadcrumb"
import { getMonitorPlatform } from "./monitor-context"
import { scrubValue } from "./sentry-privacy"
import { getSentry, isSentryEnabled } from "./sentry-state"

export type JourneyOutcome = "start" | "success" | "fail"

const ALLOWED_JOURNEY_ATTRS = new Set([
  "strategy",
  "stage",
  "feature",
  "flag",
  "code",
  "reason",
  "vehicleType",
  "cityId",
  "restoId",
  "orderId",
  "duration_ms",
])

function sanitizeAttrs(
  attrs?: Record<string, string | number | boolean | undefined | null>
) {
  if (!attrs) return undefined

  return Object.fromEntries(
    Object.entries(attrs)
      .filter(([key, value]) => {
        return (
          value !== undefined &&
          value !== null &&
          ALLOWED_JOURNEY_ATTRS.has(key)
        )
      })
      .map(([key, value]) => [key, value])
  )
}

function recordJourneyMetric(
  journey: string,
  outcome: JourneyOutcome,
  platform: string,
  attrs?: Record<string, string | number | boolean>
) {
  if (!isSentryEnabled()) return

  const metrics = getSentry()?.metrics as
    | {
        count?: (
          name: string,
          value: number,
          options?: { attributes?: Record<string, string | number | boolean> }
        ) => void
      }
    | undefined

  metrics?.count?.(`journey.${journey}.${outcome}`, 1, {
    attributes: {
      journey,
      outcome,
      platform,
      ...attrs,
    },
  })
}

export function trackJourney(
  journey: string,
  outcome: JourneyOutcome,
  attrs?: Record<string, string | number | boolean | undefined | null>
) {
  const event = `${journey}_${outcome}`
  const sanitized = sanitizeAttrs(attrs)
  const platform = getMonitorPlatform()
  const breadcrumbData = scrubValue({
    platform,
    journey,
    outcome,
    ...sanitized,
  }) as Record<string, unknown> | undefined

  addMonitorBreadcrumb({
    category: "business",
    level: outcome === "fail" ? "error" : "info",
    message: event,
    data: breadcrumbData,
  })

  recordJourneyMetric(
    journey,
    outcome,
    platform,
    sanitized as Record<string, string | number | boolean> | undefined
  )

  if (import.meta.env.DEV && outcome !== "start") {
    console.info("[JOURNEY]", event, breadcrumbData)
  }
}
